import { useCallback, useState } from 'react';
import * as Sentry from '@sentry/browser';

import useComponentMounted from '../../hooks/useComponentMounted';
import { getChallengePropagation } from '../utils/challengePropagation';

/**
 * Holds a template save (a program, a meal plan) behind a notice when a challenge
 * that has not started hands the template out: the server then rewrites what the
 * signed-up clients were given, and the coach should know before saving.
 *
 * The notice is informational — propagation happens server-side either way — so
 * a failed lookup never blocks the save.
 *
 * @param {(...args: any[]) => Promise<void>} save - Performs the save with the
 * arguments given to `requestSave`.
 * @returns {{
 *   isChecking: boolean,
 *   pendingPropagation: ?{challengeName: string, startDate: string},
 *   requestSave: (template: ?{programId?: string, mealPlanId?: string}, ...args: any[]) => Promise<void>,
 *   confirmSave: () => Promise<void>,
 *   cancelSave: () => void,
 * }} `requestSave` saves straight away when `template` is null (nothing to look
 * up) or no attached challenge is open; otherwise it parks the save behind
 * `pendingPropagation` until `confirmSave` or `cancelSave`.
 */
const useChallengeSaveConfirmation = (save) => {
  const isComponentMountedRef = useComponentMounted();
  const [isChecking, setIsChecking] = useState(false);
  const [pendingSave, setPendingSave] = useState(null);

  const requestSave = useCallback(async (template, ...args) => {
    if (template) {
      setIsChecking(true);
      try {
        const propagation = await getChallengePropagation(template);
        if (!isComponentMountedRef.current) {
          return;
        }
        setIsChecking(false);
        if (propagation) {
          setPendingSave({ propagation, args });
          return;
        }
      } catch (error) {
        Sentry.captureException(error);
        if (isComponentMountedRef.current) {
          setIsChecking(false);
        }
      }
    }

    await save(...args);
  }, [save, isComponentMountedRef]);

  const confirmSave = useCallback(async () => {
    const { args } = pendingSave;
    setPendingSave(null);
    await save(...args);
  }, [pendingSave, save]);

  const cancelSave = useCallback(() => setPendingSave(null), []);

  return {
    isChecking,
    pendingPropagation: pendingSave?.propagation ?? null,
    requestSave,
    confirmSave,
    cancelSave,
  };
};

export default useChallengeSaveConfirmation;
